import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { buildTruckPlanColumns } from '../components/build-truck-plan-columns';
import { ExcludedItems } from '../components/ExcludedItems';
import { OverrideModal } from '../components/OverrideModal';
import { TruckPlanTable } from '../components/TruckPlanTable';
import { useTruckServicePlan } from '../hooks/use-truck-service-plan';
import type { TruckServiceItemView } from '../truck-service.api';

/**
 * The Service plan tab of a truck's own page: what this truck is serviced for and how often. It is the truck's class's items with this truck's
 * own changes applied; the service done lives on the Service records tab. Fleet's `TruckDetailPage` owns the header and the truck.
 */
export function TruckServicePlanTab({ truckId, classCode }: { truckId: string; classCode: string }) {
  const plan = useTruckServicePlan(truckId);
  const canManage = useCanManageService();
  const [overrideFor, setOverrideFor] = useState<TruckServiceItemView | undefined>(undefined);

  if (plan.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (plan.state.status === 'loading') return <TableSkeleton label="Loading service plan" withSearch />;
  if (plan.state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this service plan" description="Something went wrong loading the plan. Try reloading the page." />
    );
  }

  const actions = canManage
    ? {
        onOverride: (row: TruckServiceItemView) => {
          plan.resetSaveError();
          setOverrideFor(row);
        },
        onRemoveOverride: (row: TruckServiceItemView) => plan.removeOverride({ id: row.serviceItemId, name: row.name }),
      }
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-muted">
        What this truck is serviced for: the service items of class {classCode}, with this truck's own changes. Which classes an item applies to is set under
        Maintenance.
      </p>
      <TruckPlanTable
        items={plan.state.page.items}
        total={plan.state.page.total}
        columns={buildTruckPlanColumns({ list: plan.list, actions })}
        list={plan.list}
        isRefreshing={plan.isRefreshing}
        emptyDescription="No service item applies to this truck's class yet. Pick the class on a service item under Maintenance."
      />
      <ExcludedItems items={plan.state.excluded} onRestore={canManage ? plan.removeOverride : undefined} />
      <OverrideModal item={overrideFor} error={plan.saveError} submitting={plan.saving} onSubmit={plan.setOverride} onClose={() => setOverrideFor(undefined)} />
    </div>
  );
}
